/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import React from 'react';
import { Button, ButtonGroup, Image, Dropdown } from 'react-bootstrap';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { useBreakpoint } from '@vlinder-web/breakpoint-hook-widget-react';
import {
  BreadcrumbWidget,
  IBreadcrumbItemProps,
} from '../../../../widgets/breadcrumb-widget-react';

import * as S from './styles';

export interface IHeaderProps {
  breadcrumbItems?: IBreadcrumbItemProps[];
  headerTitle?: string;
  headerDescription?: string;
  refreshButton?: {
    id: string;
    label: string;
    icon?: string;
  };
  headerAction1?: {
    id: string;
    label: string;
    icon?: string;
  };
  onHeaderAction1Click?(id: string): void;
  headerAction2?: {
    id: string;
    label: string;
  }[];
  onHeaderAction2Click?(id: string): void;
  onRefreshClick?(): void;
  headerMoreActionLabel?: string;
  headerMoreActionIcon?: string;
  refreshButtonStyle?: any;
  headerDDActionItems?: {
    id: string;
    icon?: string;
    label: string;
  }[];
  onHeaderDDActionItemClick?(id: string): void;
}

const isValidUrl = (urlString: string) => {
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

const _getIcon = (Icon: any) => {
  let _icon: any = [];
  if (typeof Icon === 'string') {
    if (isValidUrl(Icon)) {
      _icon.push(
        <Image
          key={`image-icon`}
          src={Icon}
          width={60}
          height={60}
          className="icon icon-xs me-2"
        />
      );
    } else {
      const HeroIcon = require('@heroicons/react/solid');
      const NavIcon = HeroIcon[Icon];
      if (NavIcon) {
        _icon.push(
          <NavIcon key={`hero-icon`} src={Icon} className="icon icon-xs me-2" />
        );
      }
    }
    return _icon;
  }
  if (Icon) {
    _icon.push(<Icon key={`hero-icon-2`} className="icon icon-xs me-2" />);
  }
  return _icon;
};

const _getDDIcon = (Icon: any) => {
  let _icon: any = [];
  if (typeof Icon === 'string') {
    if (isValidUrl(Icon)) {
      _icon.push(
        <Image
          key={`image-icon`}
          src={Icon}
          width={60}
          height={60}
          className="dropdown-icon text-gray-400 me-2"
        />
      );
    } else {
      const HeroIcon = require('@heroicons/react/solid');
      const NavIcon = HeroIcon[Icon];
      if (NavIcon) {
        _icon.push(
          <NavIcon
            key={`hero-icon`}
            src={Icon}
            className="dropdown-icon text-gray-400 me-2"
          />
        );
      }
    }
    return _icon;
  }
  if (Icon) {
    _icon.push(
      <Icon key={`hero-icon-2`} className="dropdown-icon text-gray-400 me-2" />
    );
  }
  return _icon;
};

export const Header = ({
  breadcrumbItems,
  headerTitle,
  headerDescription,
  headerAction1,
  headerAction2,
  refreshButton,
  onRefreshClick,
  onHeaderAction1Click,
  onHeaderAction2Click,
  refreshButtonStyle,
  headerMoreActionLabel,
  headerMoreActionIcon,
  headerDDActionItems,
  onHeaderDDActionItemClick,
}: IHeaderProps) => {
  const breakpoint = useBreakpoint();
  const getButtonMargin = () => {
    let _style: any = {};
    _style.marginTop = '5px';
    return _style;
  };
  if (headerTitle) {
    return (
      <>
        <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center p-4">
          <div className="d-block mb-4 mb-md-0">
            {breadcrumbItems && <BreadcrumbWidget items={breadcrumbItems} />}
            <h4>{headerTitle}</h4>
            {headerDescription && <p className="mb-0">{headerDescription}</p>}
          </div>
          {onRefreshClick && (
            <div className="btn-toolbar mb-3 mb-md-0">
              {refreshButton && (
                <ButtonGroup
                  className="ms-2 ms-lg-3"
                  style={refreshButtonStyle}
                >
                  <Button
                    variant="outline-gray-600"
                    size="sm"
                    className="d-inline-flex align-items-center"
                    onClick={onRefreshClick}
                  >
                    {_getIcon(refreshButton?.icon)} {refreshButton?.label}
                  </Button>
                </ButtonGroup>
              )}
              {headerAction1 && (
                <ButtonGroup
                  className="ms-2 ms-lg-3"
                  style={refreshButtonStyle}
                >
                  <Button
                    variant="primary"
                    size="sm"
                    className="d-inline-flex align-items-center"
                    onClick={onHeaderAction1Click?.bind(null, headerAction1.id)}
                  >
                    {_getIcon(headerAction1?.icon)} {headerAction1?.label}
                  </Button>
                </ButtonGroup>
              )}
              <ButtonGroup className="ms-2 ms-lg-3" style={getButtonMargin()}>
                {headerAction2?.map((item, index) => (
                  <Button
                    key={`header-action-${item?.id}-${index}`}
                    variant="outline-gray-600"
                    size="sm"
                    onClick={onHeaderAction2Click?.bind(null, item.id)}
                  >
                    {item.label}
                  </Button>
                ))}
              </ButtonGroup>
              {headerMoreActionLabel && (
                <div className="d-flex" style={getButtonMargin()}>
                  <Dropdown>
                    <Dropdown.Toggle as={Button} variant="gray-800">
                      {_getIcon(headerMoreActionIcon)}
                      {headerMoreActionLabel}
                      <ChevronDownIcon className="icon icon-xs ms-1" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="dashboard-dropdown dropdown-menu-start mt-2 py-1">
                      {headerDDActionItems?.map((dd, index) => (
                        <Dropdown.Item
                          className="d-flex align-items-center"
                          key={`header-dd-action-item-${dd?.id}-${index}`}
                          onClick={onHeaderDDActionItemClick?.bind(null, dd.id)}
                        >
                          {_getDDIcon(dd?.icon)} {dd?.label}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  }
  return null;
};

Header.defaultProps = {
  refreshButtonStyle: {
    marginRight: '1rem',
    marginTop: '5px',
  },
};
